import { useEffect } from 'react';

/**
 * The soft keyboard signal — ADR-0101 §6.4.
 *
 * While a phone's on-screen keyboard is up it takes roughly half the viewport, and the two
 * boxes that are pinned to the bottom of this layout — the dock (`Toolbar.module.css`) and the
 * diagnostics sheet (`Drawer.module.css`) — are exactly the two that must get out of the way,
 * or the caret the user is typing at ends up behind them. Both of those stylesheets already
 * key on `:root[data-keyboard='open']`. THIS HOOK IS THE ONLY THING THAT WRITES IT.
 *
 * ═══ WHY THIS IS JS AT ALL ═══
 *
 * It is the product's only JS-driven layout change, and it is here because CSS cannot see the
 * keyboard. `100dvh` shrinks with the *visual* viewport on some engines and not on others,
 * `env(keyboard-inset-height)` needs the VirtualKeyboard API (Chromium only, and only after
 * `navigator.virtualKeyboard.overlaysContent = true`, which changes how the whole page is laid
 * out), and `interactive-widget=resizes-content` — which `index.html` does set — resizes the
 * layout viewport without telling CSS *why* it changed. The three conditions below are the
 * portable reading, they need no new dependency, and nothing is persisted.
 *
 * ═══ THE THREE CONDITIONS, AND WHY EACH ONE IS NEEDED ═══
 *
 * 1. `(pointer: coarse)` — a desktop window resized short is not a keyboard, and hiding the
 *    toolbar there would be a magic trick with no cause.
 * 2. A TEXT-ENTRY ELEMENT HAS FOCUS. §6.4 words this as "the editor has focus", and that is
 *    what it means in this product; it is *implemented* as "the focused element accepts typed
 *    text" because `ui/**` may not know that the editor is CodeMirror, let alone match on
 *    `.cm-content` (gate 2, ADR-0005). `contenteditable` — which is what CodeMirror's content
 *    element is — plus `<input>` and `<textarea>` is the same set, stated in DOM terms.
 * 3. `visualViewport.height < window.innerHeight * 0.75` — the measurement itself. A keyboard
 *    takes far more than a quarter of a phone; a URL bar collapsing takes far less.
 *
 * All three must hold, so a phone that is merely scrolled, or focused on a button, keeps its
 * dock. The attribute is removed the moment any of them stops holding, and on unmount.
 */

/** A keyboard eats much more than a quarter of the viewport; a collapsing URL bar does not. */
const SHRUNK_BELOW = 0.75;

/** Accepts typed text, so raising a keyboard is what focusing it does. */
function isTextEntry(node: EventTarget | null): boolean {
  if (!(node instanceof HTMLElement)) return false;
  return node.isContentEditable || node.matches('input, textarea');
}

export function useVirtualKeyboard(): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    // No `visualViewport` means no measurement, and a guess here would hide the toolbar on a
    // browser that never had a soft keyboard. jsdom is one such host, which is why the test
    // for this hook installs its own.
    if (!viewport) return undefined;

    const root = document.documentElement;
    const coarse = window.matchMedia('(pointer: coarse)');

    /**
     * `focused` is passed in rather than read from `document.activeElement` because the caller
     * sometimes knows better: during a `focusout` the active element is already `<body>`, and
     * the element that is about to receive focus is the event's `relatedTarget`. Reading the
     * document instead would drop the attribute for one frame on every tap that moves the
     * caret from one text field to another, and the dock would flash back into the layout.
     */
    const apply = (focused: EventTarget | null): void => {
      const open =
        coarse.matches &&
        isTextEntry(focused) &&
        viewport.height < window.innerHeight * SHRUNK_BELOW;
      if (open) root.dataset.keyboard = 'open';
      else delete root.dataset.keyboard;
    };

    const onViewportChange = (): void => {
      apply(document.activeElement);
    };
    const onFocusIn = (event: FocusEvent): void => {
      apply(event.target);
    };
    const onFocusOut = (event: FocusEvent): void => {
      apply(event.relatedTarget);
    };

    onViewportChange();
    // `scroll` as well as `resize`: iOS reports the shrunk viewport through a scroll of the
    // visual viewport as often as it reports a resize of it.
    viewport.addEventListener('resize', onViewportChange);
    viewport.addEventListener('scroll', onViewportChange);
    document.addEventListener('focusin', onFocusIn);
    document.addEventListener('focusout', onFocusOut);

    return () => {
      viewport.removeEventListener('resize', onViewportChange);
      viewport.removeEventListener('scroll', onViewportChange);
      document.removeEventListener('focusin', onFocusIn);
      document.removeEventListener('focusout', onFocusOut);
      // The attribute belongs to this hook's lifetime. Leaving it behind would hide the dock
      // of whatever renders next.
      delete root.dataset.keyboard;
    };
  }, []);
}

export default useVirtualKeyboard;
